import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  ComponentRef,
  inject,
  input,
  effect,
} from '@angular/core';
import { fromEvent, takeUntil, Subject, debounceTime } from 'rxjs';
import { TreeListUtilsService } from '@app/orders/services/tree-list-utils.service';
import { TreeListActionsComponent } from '../components/utils/tree-list/tree-list-actions/tree-list-actions.component';
import { TreeListAction } from '../models/dx-tree-list.model';

@Directive({
  selector: '[appTreeListActions]',
  standalone: false,
})
export class TreeListActionsDirective implements OnInit, OnDestroy {
  private readonly container = inject(ViewContainerRef);
  private readonly utils = inject(TreeListUtilsService);
  actions = input<TreeListAction[]>([]);
  fixedActions = input<TreeListAction[]>([]);
  readonly actionsEffect = effect(() => {
    const actions = this.actions();
    if (this._actionsComponent) {
      this._actionsComponent.setInput('actions', actions);
    }
  });
  readonly fixedActionsEffect = effect(() => {
    const fixedActions = this.fixedActions();
    if (this._actionsComponent) {
      this._actionsComponent.setInput('fixedActions', fixedActions);
    }
  });
  data = input<any>();
  row = input<HTMLElement>();
  rowIndex = input<number>();
  private _componentElementRef: ElementRef;
  private _actionsComponent: ComponentRef<TreeListActionsComponent>;
  private _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.createActionBar();
    this.addEventsToElement();
  }

  private createActionBar() {
    if (this.row) {
      this._actionsComponent = this.container.createComponent(TreeListActionsComponent);
      this._actionsComponent.setInput('actions', this.actions());
      this._actionsComponent.setInput('fixedActions', this.fixedActions());
      this._actionsComponent.setInput('data', this.data());
      this._actionsComponent.setInput('rowIndex', this.rowIndex());
      this._actionsComponent.setInput('rowElement', this.row());
      this._actionsComponent.setInput('hidden', true);
      this._componentElementRef = this.getComponentElementRef();
    }
  }

  public getComponentElementRef() {
    return this._actionsComponent?.instance?.ref;
  }

  private addEventsToElement() {
    this.listenToRowMouseover();
    this.listenToRowMouseleave();
    this.listenToRowClick();
    this.forceHideActionsOnBodyLeave();
  }

  private listenToRowMouseleave() {
    fromEvent(this.row(), 'mouseleave', { passive: true })
      .pipe(debounceTime(50), takeUntil(this._destroy$))
      .subscribe({
        next: (e: MouseEvent) => {
          if (
            !this._componentElementRef?.nativeElement?.contains(e.relatedTarget) &&
            !this.isMouseInDropdownMenu(e.relatedTarget)
          ) {
            this.utils.hideActionBar(this._actionsComponent);
          }
        },
      });
  }

  private listenToRowMouseover() {
    fromEvent(this.row(), 'mouseover', { passive: true })
      .pipe(debounceTime(50), takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.utils.sendRowHoverPayload(this._actionsComponent);
        },
      });
  }

  private listenToRowClick() {
    fromEvent(this.row(), 'mousedown')
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (e: MouseEvent) => {
          const target = e.target as EventTarget | null;
          if (this._componentElementRef?.nativeElement?.contains(target)) {
            return;
          }
          if (this.isMouseInDropdownMenu(target)) {
            return;
          }
          this.utils.hideActionBar(this._actionsComponent);
        },
      });
  }

  private forceHideActionsOnBodyLeave() {
    if (this.rowIndex() === 0) {
      fromEvent(this.row()?.parentElement, 'mouseleave', { passive: true })
        .pipe(debounceTime(50), takeUntil(this._destroy$))
        .subscribe({
          next: (e: MouseEvent) => {
            if (!this.isMouseInDropdownMenu(e.relatedTarget)) {
              this.utils.hideLastActionOnTreeLeave();
            }
          },
        });
    }
  }

  private isMouseInDropdownMenu(target: EventTarget) {
    return target?.['id'] === 'btn-group-overlay';
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
